import React, { Component } from "react";
import * as yup from "yup";
import { Link, withRouter } from "react-router-dom";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import cx from "clsx";
import Sidebar from "../../../../_legacy/Containers/Settings/sidebar.js";
import {
  fetchSelectedConsent,
  createConsent,
  deleteConsent,
  updateSelectedConsent,
} from "../../../../Actions/Settings/settingsActions.js";
import Loader from "../../../../Components/Common/Loader.js";
import { FileParser } from "../../../../widgets/FileParser/FileParser";
import { uiNotification } from "../../../../services/UINotificationService";
import { FORM_TYPE } from "../consts.js";
import { Tabs } from "../../../../shared/Tabs/Tabs.js";
import { TextEditor } from "../../../../shared/TextEditor/TextEditor.js";
import classes from "../ManageClinics.module.scss";
import { MultiPostFromFiles } from "../MultiPostFromFiles/MultiPostFromFiles.js";
import { withMultiPostFromFiles } from "../MultiPostFromFiles/withMultiPostFromFiles.js";
import { MultiPostFromFilesContent } from "./components/MultiPostFromFilesContent.js";
import { http } from "../../../../services/HttpService.js";
import { HTTP_ENDPOINTS } from "../../../../consts/api.js";
import { Button } from "../../../../shared/Button/Button.js";
import { ASSIGN_TO_SERVICES_MODEL_TYPE } from "../../../../api/mutations/useAssignServiceToModelMutation.js";
import { SERVICES_BY_CATEGORIES_MODEL_TYPE } from "../../../../api/queries/useServicesGroupedByCategoriesQuery.js";
import { ModalAssignServices } from "../../../../widgets/ModalAssignServices/ModalAssignServices.js";

const CONSENT_PAGE_WORD_LIMIT = 500;
const CONSENT_PAGEBREAK_CLASS = "consent-pagebreak-separator";
const CONSENT_CURSOR_MARKER_ATTRIBUTE = "data-consent-cursor-marker";
const CONSENT_PAGEBREAK_COMMENT = /<!--\s*pagebreak\s*-->/gi;
const CONSENT_PAGEBREAK_IMAGE = /<img\b[^>]*\bmce-pagebreak\b[^>]*>/gi;
const CONSENT_PAGEBREAK_SPAN =
  /<span\b(?=[^>]*\bdata-consent-pagebreak=["']true["'])[^>]*>[\s\S]*?<\/span>/gi;
const CONSENT_CURSOR_MARKER_SPAN =
  /<span\b(?=[^>]*\bdata-consent-cursor-marker=["']true["'])[^>]*>[\s\S]*?<\/span>/gi;
const TINYMCE_BOOKMARK_CHARACTER = /\ufeff/g;
const HTML_MARKUP_MATCHER =
  /<\/?[A-Za-z][^>]*>|&(?:[A-Za-z][A-Za-z0-9]+|#\d+|#x[\dA-Fa-f]+);/g;
const SENTENCE_END_MARKS = new Set([".", "?", "!"]);
const CONSENT_ABBREVIATIONS = new Set([
  "a.m",
  "approx",
  "appt",
  "apr",
  "aug",
  "co",
  "corp",
  "dec",
  "dept",
  "dr",
  "e.g",
  "est",
  "etc",
  "feb",
  "fig",
  "figs",
  "i.e",
  "inc",
  "jan",
  "jr",
  "jul",
  "jun",
  "ltd",
  "mar",
  "max",
  "min",
  "misc",
  "mr",
  "mrs",
  "ms",
  "mx",
  "no",
  "nos",
  "nov",
  "oct",
  "p.m",
  "prof",
  "ref",
  "refs",
  "sep",
  "sept",
  "sr",
  "st",
  "u.k",
  "u.s",
  "vs",
]);
const EXCLUDED_PAGEBREAK_TAGS = {
  SCRIPT: true,
  STYLE: true,
};
const CONSENT_TEXT_SEPARATOR_TAGS = new Set([
  "ADDRESS",
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "DD",
  "DIV",
  "DL",
  "DT",
  "FIGCAPTION",
  "FIGURE",
  "FOOTER",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HEADER",
  "HR",
  "LI",
  "MAIN",
  "NAV",
  "OL",
  "P",
  "PRE",
  "SECTION",
  "TABLE",
  "TBODY",
  "TD",
  "TFOOT",
  "TH",
  "THEAD",
  "TR",
  "UL",
]);
const CONSENT_PAGEBREAK_CONTENT_STYLE = `
  .${CONSENT_PAGEBREAK_CLASS} {
    border-top: 1px dashed #188bf6;
    display: block;
    font-style: normal !important;
    font-weight: 400 !important;
    height: 0;
    line-height: 0;
    margin: 16px 0;
    position: relative;
    text-decoration: none !important;
    user-select: none;
    width: 100%;
  }
`;

const stripConsentPageBreaks = (html = "") =>
  html
    .replace(CONSENT_PAGEBREAK_COMMENT, "")
    .replace(CONSENT_PAGEBREAK_IMAGE, "")
    .replace(CONSENT_PAGEBREAK_SPAN, " ")
    .replace(CONSENT_CURSOR_MARKER_SPAN, "")
    .replace(TINYMCE_BOOKMARK_CHARACTER, "");

const shouldProcessTextNode = (node) => {
  const parentNode = node.parentNode;
  const normalizedValue = node.nodeValue?.replace(/\u00a0/g, " ");

  return (
    normalizedValue &&
    !EXCLUDED_PAGEBREAK_TAGS[parentNode?.nodeName || ""] &&
    !parentNode?.closest?.("[data-consent-pagebreak='true']") &&
    !parentNode?.closest?.(`[${CONSENT_CURSOR_MARKER_ATTRIBUTE}="true"]`)
  );
};

const shouldSkipConsentTextElement = (node) =>
  EXCLUDED_PAGEBREAK_TAGS[node.nodeName] ||
  node.closest?.("[data-consent-pagebreak='true']") ||
  node.closest?.(`[${CONSENT_CURSOR_MARKER_ATTRIBUTE}="true"]`);

const getAdjacentTextNode = (node, siblingProperty, childProperty) => {
  let currentNode = node[siblingProperty];

  while (currentNode) {
    if (currentNode.nodeType === 3) {
      return currentNode;
    }

    if (
      currentNode.nodeType === 1 &&
      !shouldSkipConsentTextElement(currentNode)
    ) {
      let childNode = currentNode[childProperty];

      while (childNode) {
        if (childNode.nodeType === 3) {
          return childNode;
        }

        childNode = childNode[childProperty];
      }
    }

    currentNode = currentNode[siblingProperty];
  }

  return null;
};

const getTextTokenBeforeOffset = (text, offset) => {
  let tokenStart = offset;

  while (tokenStart > 0 && !/\s/.test(text[tokenStart - 1])) {
    tokenStart -= 1;
  }

  return text.slice(tokenStart, offset + 1);
};

const isConsentAbbreviationBoundary = (text, punctuationIndex) => {
  const token = getTextTokenBeforeOffset(text, punctuationIndex);
  const normalizedToken = token.toLowerCase().replace(/\.+$/, "");

  return (
    CONSENT_ABBREVIATIONS.has(normalizedToken) || /^(?:[A-Z]\.)+$/.test(token)
  );
};

const getConsentSentenceBoundaryOffsets = (text = "") => {
  const boundaries = [];

  for (let index = 0; index < text.length; index += 1) {
    if (!SENTENCE_END_MARKS.has(text[index])) {
      continue;
    }

    const nextCharacter = text[index + 1];
    const isSentenceEnd =
      typeof nextCharacter === "undefined" || /\s/.test(nextCharacter);

    if (isSentenceEnd && !isConsentAbbreviationBoundary(text, index)) {
      boundaries.push(index + 1);
    }
  }

  return boundaries;
};

const getNextPageTextStartOffset = (text, offset) => {
  let nextOffset = offset;

  while (nextOffset < text.length && /\s/.test(text[nextOffset])) {
    nextOffset += 1;
  }

  return nextOffset;
};

const getIgnoredMarkupRanges = (text = "") => {
  const ranges = [];
  HTML_MARKUP_MATCHER.lastIndex = 0;
  let match = HTML_MARKUP_MATCHER.exec(text);

  while (match) {
    ranges.push({
      startOffset: match.index,
      endOffset: match.index + match[0].length,
    });
    match = HTML_MARKUP_MATCHER.exec(text);
  }

  HTML_MARKUP_MATCHER.lastIndex = 0;

  return ranges;
};

const isAsciiLetter = (character) => {
  const charCode = character.charCodeAt(0);

  return (
    (charCode >= 65 && charCode <= 90) || (charCode >= 97 && charCode <= 122)
  );
};

const isDigit = (character) => {
  const charCode = character.charCodeAt(0);

  return charCode >= 48 && charCode <= 57;
};

const isConsentWordCharacter = (character) => {
  const charCode = character.charCodeAt(0);

  return (
    (charCode >= 48 && charCode <= 57) ||
    (charCode >= 65 && charCode <= 90) ||
    (charCode >= 97 && charCode <= 122) ||
    (charCode >= 0x00c0 && charCode <= 0x00d6) ||
    (charCode >= 0x00d8 && charCode <= 0x00f6) ||
    (charCode >= 0x00f8 && charCode <= 0x00ff)
  );
};

const isConsentWordConnector = (character) =>
  character === "'" || character === "\u2019" || character === "-";

const getInitialismEndOffset = (text, startOffset) => {
  let offset = startOffset;
  let pairCount = 0;

  while (
    offset + 1 < text.length &&
    isAsciiLetter(text[offset]) &&
    text[offset + 1] === "."
  ) {
    pairCount += 1;
    offset += 2;
  }

  return pairCount >= 2 ? offset : null;
};

const getDottedNumberEndOffset = (text, startOffset) => {
  if (!isDigit(text[startOffset])) {
    return null;
  }

  let offset = startOffset;
  let dottedPartCount = 0;

  while (offset < text.length && isDigit(text[offset])) {
    offset += 1;
  }

  while (text[offset] === "." && isDigit(text[offset + 1] || "")) {
    dottedPartCount += 1;
    offset += 1;

    while (offset < text.length && isDigit(text[offset])) {
      offset += 1;
    }
  }

  return dottedPartCount ? offset : null;
};

const getPlainWordEndOffset = (text, startOffset) => {
  if (!isConsentWordCharacter(text[startOffset])) {
    return null;
  }

  let offset = startOffset + 1;

  while (offset < text.length) {
    if (isConsentWordCharacter(text[offset])) {
      offset += 1;
      continue;
    }

    if (
      isConsentWordConnector(text[offset]) &&
      isConsentWordCharacter(text[offset + 1] || "")
    ) {
      offset += 2;

      while (offset < text.length && isConsentWordCharacter(text[offset])) {
        offset += 1;
      }

      continue;
    }

    break;
  }

  return offset;
};

const getConsentWordEndOffset = (text, startOffset) =>
  getInitialismEndOffset(text, startOffset) ||
  getDottedNumberEndOffset(text, startOffset) ||
  getPlainWordEndOffset(text, startOffset);

const getWordLimitOffset = (text, wordRange) => {
  let offset = wordRange.endOffset;

  while (offset < text.length && SENTENCE_END_MARKS.has(text[offset])) {
    offset += 1;
  }

  return offset;
};

const getConsentWordRanges = (text = "") => {
  const ignoredMarkupRanges = getIgnoredMarkupRanges(text);
  const wordRanges = [];
  let ignoredMarkupRangeIndex = 0;
  let offset = 0;

  while (offset < text.length) {
    const ignoredMarkupRange = ignoredMarkupRanges[ignoredMarkupRangeIndex];

    if (ignoredMarkupRange?.endOffset <= offset) {
      ignoredMarkupRangeIndex += 1;
      continue;
    }

    if (
      ignoredMarkupRange &&
      offset >= ignoredMarkupRange.startOffset &&
      offset < ignoredMarkupRange.endOffset
    ) {
      offset = ignoredMarkupRange.endOffset;
      continue;
    }

    const endOffset = getConsentWordEndOffset(text, offset);

    if (endOffset) {
      wordRanges.push({
        startOffset: offset,
        endOffset,
      });

      offset = endOffset;
      continue;
    }

    offset += 1;
  }

  return wordRanges;
};

const getConsentPageBreakOffsets = (text = "") => {
  const boundaries = getConsentSentenceBoundaryOffsets(text);
  const wordRanges = getConsentWordRanges(text);
  const pageBreakOffsets = [];
  let pageStartWordIndex = 0;

  while (pageStartWordIndex + CONSENT_PAGE_WORD_LIMIT < wordRanges.length) {
    const pageStartOffset = wordRanges[pageStartWordIndex].startOffset;
    const pageLimitOffset = getWordLimitOffset(
      text,
      wordRanges[pageStartWordIndex + CONSENT_PAGE_WORD_LIMIT - 1],
    );

    let pageBreakOffset = null;

    boundaries.forEach((boundaryOffset) => {
      if (
        boundaryOffset > pageStartOffset &&
        boundaryOffset <= pageLimitOffset
      ) {
        pageBreakOffset = boundaryOffset;
      }
    });

    if (!pageBreakOffset) {
      pageBreakOffset = boundaries.find(
        (boundaryOffset) => boundaryOffset > pageLimitOffset,
      );
    }

    if (!pageBreakOffset || pageBreakOffset >= text.length) {
      break;
    }

    pageBreakOffsets.push(pageBreakOffset);

    const nextPageStartOffset = getNextPageTextStartOffset(
      text,
      pageBreakOffset,
    );
    const nextPageStartWordIndex = wordRanges.findIndex(
      (wordRange) => wordRange.startOffset >= nextPageStartOffset,
    );

    if (
      nextPageStartWordIndex === -1 ||
      nextPageStartWordIndex <= pageStartWordIndex
    ) {
      break;
    }

    pageStartWordIndex = nextPageStartWordIndex;
  }

  return pageBreakOffsets;
};

const collectConsentTextNodes = (rootNode, doc) => {
  const textNodes = [];
  let text = "";

  const appendSeparator = () => {
    if (text && !/\s$/.test(text)) {
      text += "\n";
    }
  };

  const walkNode = (node) => {
    if (node.nodeType === doc.defaultView.Node.TEXT_NODE) {
      if (!shouldProcessTextNode(node)) {
        return;
      }

      const startOffset = text.length;
      const normalizedValue = node.nodeValue.replace(/\u00a0/g, " ");

      text += normalizedValue;

      if (normalizedValue.trim()) {
        textNodes.push({
          node,
          startOffset,
          endOffset: text.length,
        });
      }

      return;
    }

    if (node.nodeType !== doc.defaultView.Node.ELEMENT_NODE) {
      return;
    }

    if (shouldSkipConsentTextElement(node)) {
      return;
    }

    if (node.nodeName === "BR" || node.nodeName === "HR") {
      appendSeparator();
      return;
    }

    node.childNodes.forEach(walkNode);

    if (CONSENT_TEXT_SEPARATOR_TAGS.has(node.nodeName)) {
      appendSeparator();
    }
  };

  rootNode.childNodes.forEach(walkNode);

  return {
    text,
    textNodes,
  };
};

const createConsentPageBreakElement = (doc) => {
  const pageBreak = doc.createElement("span");

  pageBreak.className = CONSENT_PAGEBREAK_CLASS;
  pageBreak.dataset.consentPagebreak = "true";
  pageBreak.contentEditable = "false";
  pageBreak.dataset.mceContenteditable = "false";

  return pageBreak;
};

const isEmptyConsentParagraph = (node) => {
  if (node?.nodeName !== "P") {
    return false;
  }

  const text = (node.textContent || "")
    .replace(/\u00a0/g, "")
    .replace(TINYMCE_BOOKMARK_CHARACTER, "")
    .trim();

  if (text) {
    return false;
  }

  return Array.from(node.childNodes).every((childNode) => {
    if (childNode.nodeType === 3) {
      return !childNode.nodeValue
        ?.replace(/\u00a0/g, "")
        .replace(TINYMCE_BOOKMARK_CHARACTER, "")
        .trim();
    }

    return childNode.nodeType === 1 && childNode.nodeName === "BR";
  });
};

const removeEmptyParagraphsAfterConsentPageBreaks = (rootNode) => {
  rootNode
    .querySelectorAll("[data-consent-pagebreak='true']")
    .forEach((pageBreak) => {
      let nextElement = pageBreak.parentElement?.nextElementSibling;

      while (isEmptyConsentParagraph(nextElement)) {
        const emptyParagraph = nextElement;

        nextElement = nextElement.nextElementSibling;
        emptyParagraph.remove();
      }
    });
};

const insertConsentPageBreaks = (textNodes, pageBreakOffsets, doc) => {
  textNodes.forEach(({ node, startOffset, endOffset }) => {
    if (!node.parentNode) {
      return;
    }

    const pageBreakPositions = pageBreakOffsets
      .filter(
        (pageBreakOffset) =>
          pageBreakOffset > startOffset && pageBreakOffset <= endOffset,
      )
      .map((pageBreakOffset) => pageBreakOffset - startOffset);

    pageBreakPositions.reverse().forEach((position) => {
      const nextTextNode = node.splitText(position);
      const pageBreak = createConsentPageBreakElement(doc);

      node.parentNode.insertBefore(pageBreak, nextTextNode);
    });
  });
};

const applyConsentPageBreaks = (rootNode, doc) => {
  const { text, textNodes } = collectConsentTextNodes(rootNode, doc);
  const pageBreakOffsets = getConsentPageBreakOffsets(text.trimEnd());

  insertConsentPageBreaks(textNodes, pageBreakOffsets, doc);
  removeEmptyParagraphsAfterConsentPageBreaks(rootNode);
};

const addConsentPageBreaks = (html = "") => {
  const cleanHtml = stripConsentPageBreaks(html);

  if (typeof document === "undefined") {
    return cleanHtml;
  }

  const container = document.createElement("div");
  container.innerHTML = cleanHtml;

  applyConsentPageBreaks(container, document);

  return container.innerHTML;
};

class CreateEditConsents extends Component {
  constructor(props) {
    super(props);
    const languageData = JSON.parse(localStorage.getItem("languageData"));

    this.state = {
      create_edit_consent_name:
        languageData.settings["create_edit_consent_name"],
      edit_consent: languageData.settings["edit_consent"],
      add_consent: languageData.settings["add_consent"],
      settings_add_consent: languageData.settings["settings_add_consent"],
      delete_confirmation: languageData.global["delete_confirmation"],
      create_edit_consent_body:
        languageData.settings["create_edit_consent_body"],
      consent_delete_warning: languageData.settings["consent_delete_warning"],
      yes_option: languageData.settings["yes_option"],
      no_option: languageData.settings["no_option"],

      consent_name: "",
      consent_large_description: "",
      consent_editor_description: "",
      consent_editor_revision: 0,
      id: this.props.match.params.id,
      showLoader: false,
      hideBtns: false,
      showModal: false,
      initials_per_page: false,
      witness_signature: false,
      formType: FORM_TYPE.single,
      editorFiles: [],
      isAssignToServicesModalOpen: false,
      assignedServiceIds: [],
    };
  }

  componentDidMount() {
    const consentId = this.props.match.params.id;

    if (consentId) {
      this.fetchConsent();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.consentEditorPageBreakSyncTimeout);
  }

  fetchConsent = () => {
    this.setState({ showLoader: true });
    this.props
      .fetchSelectedConsent(this.props.match.params.id)
      .then((response) => {
        const consentDescription = stripConsentPageBreaks(
          response?.data?.consent_large_description || "",
        );

        this.setState({
          consent_name: response?.data?.consent_name || "",

          consent_large_description: consentDescription,
          consent_editor_description: addConsentPageBreaks(consentDescription),
          consent_editor_revision: this.state.consent_editor_revision + 1,

          initials_per_page: !!response?.data?.initials_per_page,

          witness_signature: !!response?.data?.witness_signature,

          assignedServiceIds:
            response?.data?.assigned_services?.map((s) => s.id) || [],
        });
      })
      .catch((response) => {
        const languageData = JSON.parse(localStorage.getItem("languageData"));
        uiNotification.error(
          languageData.global?.[response.message] ||
            "Unable to fetch consent. Try again later",
        );
      })
      .finally(() => {
        this.setState({ showLoader: false });
      });
  };

  handleEditorChange = (value) => {
    const cleanValue = stripConsentPageBreaks(value);

    if (cleanValue === this.state.consent_large_description) {
      return;
    }

    this.setState({
      consent_large_description: cleanValue,
      userChanged: true,
    });
  };

  restoreConsentEditorCursor = (editor, markerId) => {
    const marker = editor.dom.get(markerId);

    if (!marker) {
      return;
    }

    const parentNode = marker.parentNode;
    const markerIndex = editor.dom.nodeIndex(marker);

    this.scrollConsentEditorCursorIntoView(editor, marker);
    editor.dom.remove(marker);
    editor.focus();
    editor.selection.setCursorLocation(parentNode, markerIndex);
  };

  scrollConsentEditorCursorIntoView = (editor, marker) => {
    const iframe =
      editor.iframeElement ||
      editor.getContentAreaContainer?.()?.querySelector("iframe");

    marker.scrollIntoView({
      block: "nearest",
      inline: "nearest",
    });

    if (!iframe) {
      return;
    }

    const markerRect = marker.getBoundingClientRect();
    const iframeRect = iframe.getBoundingClientRect();
    const cursorTop = iframeRect.top + markerRect.top;
    const cursorBottom = iframeRect.top + markerRect.bottom;
    const scrollMargin = 80;

    if (cursorBottom > window.innerHeight - scrollMargin) {
      window.scrollBy({
        top: cursorBottom - window.innerHeight + scrollMargin,
        behavior: "auto",
      });
    } else if (cursorTop < scrollMargin) {
      window.scrollBy({
        top: cursorTop - scrollMargin,
        behavior: "auto",
      });
    }
  };

  removeConsentEditorPageBreaks = (editor) => {
    editor
      .getBody()
      ?.querySelectorAll("[data-consent-pagebreak='true']")
      .forEach((pageBreak) => {
        const previousTextNode = getAdjacentTextNode(
          pageBreak,
          "previousSibling",
          "lastChild",
        );
        const nextTextNode = getAdjacentTextNode(
          pageBreak,
          "nextSibling",
          "firstChild",
        );
        const needsSeparator =
          previousTextNode?.nodeValue &&
          nextTextNode?.nodeValue &&
          !/\s$/.test(previousTextNode.nodeValue) &&
          !/^\s/.test(nextTextNode.nodeValue);

        if (needsSeparator) {
          pageBreak.parentNode.insertBefore(
            pageBreak.ownerDocument.createTextNode(" "),
            pageBreak,
          );
        }

        pageBreak.remove();
      });
  };

  addConsentEditorPageBreaks = (editor) => {
    const body = editor.getBody();
    const doc = body?.ownerDocument;

    if (!body || !doc) {
      return;
    }

    this.removeConsentEditorPageBreaks(editor);
    applyConsentPageBreaks(body, doc);
  };

  isConsentEditorSelectionCollapsed = (editor) => {
    if (editor.selection?.isCollapsed) {
      return editor.selection.isCollapsed();
    }

    const range = editor.selection?.getRng?.();

    if (range) {
      return range.collapsed;
    }

    const selection = editor.getDoc?.()?.getSelection?.();

    return !selection || selection.isCollapsed;
  };

  syncConsentEditorPageBreaks = (editor) => {
    if (this.isSyncingConsentEditorPageBreaks) {
      return;
    }

    if (!this.isConsentEditorSelectionCollapsed(editor)) {
      return;
    }

    this.isSyncingConsentEditorPageBreaks = true;

    try {
      const markerId = `consent-pagebreak-cursor-${Date.now()}`;
      const markerHtml = `<span id="${markerId}" ${CONSENT_CURSOR_MARKER_ATTRIBUTE}="true" data-mce-type="bookmark" style="overflow:hidden;line-height:0"></span>`;

      editor.selection.setContent(markerHtml);

      this.addConsentEditorPageBreaks(editor);
      this.restoreConsentEditorCursor(editor, markerId);

      const nextCleanValue = stripConsentPageBreaks(editor.getContent());

      this.setState({
        consent_large_description: nextCleanValue,
        userChanged: true,
      });
    } finally {
      this.isSyncingConsentEditorPageBreaks = false;
    }
  };

  scheduleConsentEditorPageBreakSync = (editor, delay = 300) => {
    clearTimeout(this.consentEditorPageBreakSyncTimeout);
    this.consentEditorPageBreakSyncTimeout = setTimeout(() => {
      this.syncConsentEditorPageBreaks(editor);
    }, delay);
  };

  handleConsentEditorKeyUp = (editor, event) => {
    if (
      event.key === " " ||
      event.key === "Spacebar" ||
      event.key === "Enter"
    ) {
      clearTimeout(this.consentEditorPageBreakSyncTimeout);
      this.scheduleConsentEditorPageBreakSync(editor, 0);
      return;
    }

    const isTextInput =
      event.key?.length === 1 &&
      !event.altKey &&
      !event.ctrlKey &&
      !event.metaKey;
    const isDeleteInput = event.key === "Backspace" || event.key === "Delete";

    if (isTextInput || isDeleteInput) {
      this.scheduleConsentEditorPageBreakSync(editor);
    }
  };

  handleConsentEditorPaste = (editor) => {
    this.scheduleConsentEditorPageBreakSync(editor, 0);
  };

  handleInputChange = (event) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    this.setState({
      [event.target.name]: value,
      userChanged: true,
    });
  };

  showDeleteModal = () => {
    this.setState({ showModal: true });
  };

  dismissModal = () => {
    this.setState({ showModal: false });
  };

  deleteConsent = () => {
    this.setState({ showLoader: true, hideBtns: true });
    this.dismissModal();
    this.props
      .deleteConsent(this.state.id)
      .then(() => {
        uiNotification.success("Consent has been successfully deleted");
        this.props.history.push("/settings/consents");
      })
      .catch(() => {
        uiNotification.error("Unable to delete consent. Try again later");
      })
      .finally(() => {
        this.setState({ showLoader: false, hideBtns: false });
      });
  };

  handleSubmit = () => {
    let error = false;

    this.setState({
      consent_name_Error: false,
      consent_large_description_Error: false,
      consent_small_Description_Error: false,
    });

    if (
      typeof this.state.consent_name === "undefined" ||
      this.state.consent_name === null ||
      this.state.consent_name === "" ||
      this.state.consent_name.trim() === ""
    ) {
      this.setState({
        consent_name_Error: true,
      });
      error = true;
    }
    if (
      typeof this.state.consent_large_description === "undefined" ||
      this.state.consent_large_description === null ||
      this.state.consent_large_description === "" ||
      this.state.consent_large_description.trim() === ""
    ) {
      this.setState({
        consent_large_description_Error: true,
      });
      error = true;
    }

    if (error === true) {
      return;
    }

    const consentId = this.props.match.params.id;

    let formData = {
      consent_name: this.state.consent_name,
      consent_large_description: stripConsentPageBreaks(
        this.state.consent_large_description,
      ),
      initials_per_page: this.state.initials_per_page ? 1 : 0,
      witness_signature: this.state.witness_signature ? 1 : 0,
    };

    this.setState({
      consent_name: this.state.consent_name,
      consent_large_description: this.state.consent_large_description,
      showLoader: true,
    });

    if (consentId) {
      this.props
        .updateSelectedConsent({
          ...formData,
          consent_id: consentId,
        })
        .then(() => {
          uiNotification.success("Consent has been successfully updated");
          this.props.history.push("/settings/consents");
        })
        .catch(() => {
          uiNotification.error("Unable to update consent. Try again later");
        })
        .finally(() => {
          this.setState({
            showLoader: false,
          });
        });
    } else {
      this.props
        .createConsent(formData)
        .then(() => {
          uiNotification.success("Consent has been successfully created");
          this.props.history.push("/settings/consents");
        })
        .catch(() => {
          uiNotification.error("Unable to create consent. Try again later");
        })
        .finally(() => {
          this.setState({
            showLoader: false,
          });
        });
    }
  };

  handleChangeTab = (formType) => {
    const msg = "Your changes will be lost. Confirm?";
    const confirmMap = {
      [FORM_TYPE.single]: this.state.editorFiles.length > 0,
    };
    if ((confirmMap[formType] && confirm(msg)) || !confirmMap[formType]) {
      this.setState({ formType });
    }
  };

  handleSaveSingleFile = (file, { onRemoveFile }) => {
    this.props.multiPostFromFiles
      .post([file])
      .then(() => {
        uiNotification.success.bind(uiNotification)(
          `Successfully Created '${file.consentName}' Consent`,
        );
        onRemoveFile(file.id);
      })
      .catch((e) => {
        uiNotification.error.bind(uiNotification)(e.message);
      });
  };

  handleSaveMultipleFiles = (files, { onRemoveAll }) => {
    if (
      confirm(
        "Are you sure you want to save all files? Some of them may not parsed correctly or include some mistakes that you don't want to submit without review. Continue anyway?",
      )
    ) {
      this.props.multiPostFromFiles
        .post(files)
        .then(() => {
          uiNotification.success.bind(uiNotification)(
            "Successfully Created Consents From Multiple Files",
          );
          onRemoveAll();
          this.props.history.push("/settings/consents");
        })
        .catch((e) => {
          uiNotification.error.bind(uiNotification)(e.message);
        });
    }
  };

  render() {
    const isEditMode = Boolean(this.props.match.params.id);
    const { multiPostFromFiles } = this.props;

    return (
      <div className="main protected">
        <div id="content">
          <div className="container-fluid content setting-wrapper">
            <Sidebar />
            <div
              className={cx("setting-setion", classes.multiPostFromFilesRoot)}
            >
              <div className="setting-container">
                <div className="items-center m-b-20 justify-between">
                  <div className="items-center">
                    <div className="setting-title-default">
                      {this.props.match.params.id
                        ? this.state.edit_consent
                        : this.state.settings_add_consent}
                    </div>
                    {isEditMode && (
                      <Button
                        size="small"
                        className="m-l-20"
                        variant="outlined"
                        onClick={() =>
                          this.setState({ isAssignToServicesModalOpen: true })
                        }
                      >
                        Assign to Services
                      </Button>
                    )}
                  </div>
                  <Link
                    to="/settings/consents"
                    className="pull-right cancelAction"
                  >
                    <img src={"../../../../images/close.png"} alt="x" />
                  </Link>
                </div>
                {!isEditMode && (
                  <Tabs
                    className="f-left m-b-20"
                    active={this.state.formType}
                    options={[
                      {
                        label: "Create",
                        value: FORM_TYPE.single,
                        onClick: () => this.handleChangeTab(FORM_TYPE.single),
                      },
                      {
                        label: "Upload Files",
                        value: FORM_TYPE.multipleFromFiles,
                        onClick: () =>
                          this.handleChangeTab(FORM_TYPE.multipleFromFiles),
                      },
                    ]}
                  />
                )}
                {this.state.formType === FORM_TYPE.single ? (
                  <div className="row">
                    <div className="col-xs-12">
                      <div className="newInputFileldOuter">
                        <div className="newInputLabel">
                          {this.state.create_edit_consent_name}
                          <span className="setting-require">*</span>
                        </div>
                        <input
                          name="consent_name"
                          required="required"
                          className={
                            this.state.consent_name_Error === true
                              ? "consent_name newInputField field_error newInputField"
                              : "consent_name newInputField "
                          }
                          id="consent_name"
                          placeholder="Consent Name"
                          maxLength={255}
                          type="text"
                          autoComplete="off"
                          onChange={this.handleInputChange}
                          value={this.state.consent_name}
                        />
                      </div>
                    </div>
                    <div className="col-xs-12">
                      <TextEditor
                        key={`consent-editor-${this.state.id}-${this.state.consent_editor_revision}`}
                        placeholder="Description"
                        initialValue={this.state.consent_editor_description}
                        onChange={this.handleEditorChange}
                        init={{
                          height: 300,
                          menubar: false,
                          toolbar: "bold italic underline",
                          content_style: CONSENT_PAGEBREAK_CONTENT_STYLE,
                          setup: (editor) => {
                            editor.on("keyup", (event) => {
                              this.handleConsentEditorKeyUp(editor, event);
                            });

                            editor.on("Paste", () => {
                              this.handleConsentEditorPaste(editor);
                            });

                            editor.on("input Cut Drop Undo Redo", () => {
                              this.scheduleConsentEditorPageBreakSync(editor);
                            });
                          },
                        }}
                        className={cx({
                          field_error:
                            this.state.consent_large_description_Error === true,
                        })}
                      />
                    </div>
                    <div className="col-xs-12">
                      <p className="m-b-10 m-t-20">
                        <input
                          id="chk-initials_per_page"
                          type="checkbox"
                          className="new-check"
                          name="initials_per_page"
                          onChange={this.handleInputChange}
                          checked={
                            this.state.initials_per_page ? "checked" : false
                          }
                        />
                        <label htmlFor="chk-initials_per_page">
                          {"Would you like initials on pages?"}
                        </label>
                      </p>
                    </div>
                    <div className="col-xs-12">
                      <p className="m-b-10">
                        <input
                          id="chk-witness_signature"
                          type="checkbox"
                          className="new-check"
                          name="witness_signature"
                          onChange={this.handleInputChange}
                          checked={
                            this.state.witness_signature ? "checked" : false
                          }
                        />
                        <label htmlFor="chk-witness_signature">
                          {"Would you like witness signature at the end?"}
                        </label>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className={cx("row", classes.multiPostFromFilesRow)}>
                    <FileParser
                      withEditor
                      onError={uiNotification.error.bind(uiNotification)}
                      footerClassName={classes.multiPostFromFilesFooter}
                      renderFooter={({ isFilesSelected, onOpenEditor }) => (
                        <MultiPostFromFiles.Footer
                          isFilesSelected={isFilesSelected}
                          onOpenEditor={onOpenEditor}
                        />
                      )}
                      editor={{
                        isRemoveAllDisabled: multiPostFromFiles.isPosting,
                        isSelectFilesDisabled: multiPostFromFiles.isPosting,
                        isFilesDisabled: multiPostFromFiles.isPosting,
                        onRemoveAllCb:
                          multiPostFromFiles.resetValidationErrorLogs,
                        onRemoveFileCb:
                          multiPostFromFiles.resetValidationErrorLogs,
                        renderFooter: ({
                          currentFile,
                          files,
                          isFilesParsing,
                          onRemoveFile,
                          onRemoveAll,
                        }) => (
                          <MultiPostFromFiles.EditorFooter
                            onPostMultiple={() =>
                              this.handleSaveMultipleFiles(files, {
                                onRemoveAll,
                              })
                            }
                            onPostSingle={() =>
                              this.handleSaveSingleFile(currentFile, {
                                onRemoveFile,
                              })
                            }
                            currentFile={currentFile}
                            files={files}
                            isFilesParsing={isFilesParsing}
                            errorLogs={multiPostFromFiles.validationErrorLogs}
                            isPosting={multiPostFromFiles.isPosting}
                          />
                        ),
                        renderFilesMobileFooter: ({
                          files,
                          isFilesParsing,
                          onRemoveAll,
                        }) => (
                          <MultiPostFromFiles.EditorFilesMobileFooter
                            isFilesParsing={isFilesParsing}
                            onPostMultiple={() =>
                              this.handleSaveMultipleFiles(files, {
                                onRemoveAll,
                              })
                            }
                            errorLogs={multiPostFromFiles.validationErrorLogs}
                            isPosting={multiPostFromFiles.isPosting}
                          />
                        ),
                        renderContent: ({ currentFile, onEditFile }) => (
                          <MultiPostFromFilesContent
                            currentFile={currentFile}
                            onEditFile={onEditFile}
                            isPosting={multiPostFromFiles.isPosting}
                          />
                        ),
                        onFilesChange: (editorFiles) => {
                          this.setState({ editorFiles });
                        },
                      }}
                    />
                  </div>
                )}
              </div>
              {this.state.formType === FORM_TYPE.single && (
                <div className="footer-static">
                  {this.props.match.params.id != undefined && (
                    <input
                      className="new-red-btn pull-left confirm-model"
                      type="button"
                      value="Delete"
                      onClick={this.showDeleteModal}
                    />
                  )}
                  {!this.state.hideBtns && (
                    <button
                      type="button"
                      id="save_clinic"
                      className="new-blue-btn pull-right"
                      onClick={this.handleSubmit}
                    >
                      Save
                    </button>
                  )}
                  {!this.state.hideBtns && (
                    <Link
                      to="/settings/consents"
                      className="new-white-btn pull-right cancelAction"
                    >
                      Cancel
                    </Link>
                  )}
                </div>
              )}
              <div className={this.state.showModal ? "overlay" : ""}></div>
              <div
                id="filterModal"
                role="dialog"
                className={
                  this.state.showModal
                    ? "modal fade in displayBlock"
                    : "modal fade"
                }
              >
                <div className="modal-dialog">
                  <div className="modal-content">
                    <div className="modal-header">
                      <button
                        type="button"
                        className="close"
                        data-dismiss="modal"
                        onClick={this.dismissModal}
                      >
                        x
                      </button>
                      <h4 className="modal-title" id="model_title">
                        {this.state.delete_confirmation}
                        {this.state.showModal}
                      </h4>
                    </div>
                    <div
                      id="errorwindow"
                      className="modal-body add-patient-form filter-patient"
                    >
                      {this.state.consent_delete_warning}
                    </div>
                    <div className="modal-footer">
                      <div className="col-md-12 text-left" id="footer-btn">
                        <button
                          type="button"
                          className="btn  logout pull-right"
                          data-dismiss="modal"
                          onClick={this.dismissModal}
                        >
                          {this.state.no_option}
                        </button>
                        <button
                          type="button"
                          className="btn btn-success pull-right m-r-10"
                          data-dismiss="modal"
                          onClick={this.deleteConsent}
                        >
                          {this.state.yes_option}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Loader showLoader={this.state.showLoader} isFullWidth={true} />
        </div>
        {this.state.isAssignToServicesModalOpen && (
          <ModalAssignServices
            id={this.state.id}
            modelType={ASSIGN_TO_SERVICES_MODEL_TYPE.consent}
            servicesType={SERVICES_BY_CATEGORIES_MODEL_TYPE.all}
            pickedIds={this.state.assignedServiceIds}
            isOpen={this.state.isAssignToServicesModalOpen}
            onClose={() =>
              this.setState({ isAssignToServicesModalOpen: false })
            }
            onSuccess={() => this.fetchConsent()}
            title="Assign Services to Consent"
          />
        )}
      </div>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchSelectedConsent,
      createConsent,
      updateSelectedConsent,
      deleteConsent,
    },
    dispatch,
  );
}

export default withRouter(
  connect(
    null,
    mapDispatchToProps,
  )(
    withMultiPostFromFiles(CreateEditConsents, {
      submitFile: (file) =>
        http.post(HTTP_ENDPOINTS.postConsent(), {
          consent_large_description: file.text,
          consent_name: file.consentName,
          initials_per_page: file.isInitialsOnPage ? 1 : 0,
          witness_signature: file.isWitnessSignatureAtTheEnd ? 1 : 0,
        }),
      composeFileForSubmit: (file) => ({
        consentName: file.consentName,
        text: file.parsedContent,
        isInitialsOnPage: file.isInitialsOnPage,
        isWitnessSignatureAtTheEnd: file.isWitnessSignatureAtTheEnd,
      }),
      submitFileValidationSchema: yup.object({
        consentName: yup.string().required("Consent name is required"),
        text: yup.string().required("Consent body is required"),
        isInitialsOnPage: yup.bool(),
        isWitnessSignatureAtTheEnd: yup.bool(),
      }),
    }),
  ),
);
